import React, {useEffect, useState, useMemo, useContext, useReducer} from 'react';
import {Block} from '../block';
import classnames from 'classnames';
import {ParameterTable} from './parameter-table';
import {OpenApiContext} from '../../openapi-context';
import {Responses} from './responses';
import {RequestBody, RequestBodyDeleteButton} from './request-body';
import {merge, omit} from 'lodash';
import {OperationHeaderBlock} from './operation-header-block';
import {Collapse} from 'antd';
import {operationReducer} from './operation-reducer';

export function OperationBlock({op, className, params, path, name, color, onSave, onCancel, imageRootPath, mobile}) {
    const classes = classnames('operationBlock', className);
    const {state, dispatch} = useContext(OpenApiContext);
    const [editMode, setEditMode] = useState(false);
    const [parameters, setParameters] = useState();
    const [editedOperation, updateOperation] = useReducer(operationReducer, op);

    function handleSave() {
        dispatch({type: 'terminate'});
        setEditMode(false);
        const changeSet = {};
        if (parameters) {
            changeSet[name] = merge(editedOperation, {
                parameters: parameters.map((p) => {
                    if (p.$ref) {
                        return {$ref: p.$ref};
                    } else {
                        return omit(p, '$ref', 'key', 'type', 'tags', '$resolvedRef', 'state');
                    }
                }),
            });
        } else {
            changeSet[name] = editedOperation;
        }
        return onSave && onSave(changeSet);
    }

    function handleCancel() {
        dispatch({type: 'terminate'});
        setEditMode(false);
        updateOperation({type: 'init', op});
        return onCancel && onCancel();
    }

    const triggerEdit = () => {
        setEditMode(true);
        console.log('operation-block: changes to operation', op, path, name);
        dispatch({type: 'activate', message: `changes to operation ${path}`});
    };

    function handleAddBlock(option) {
        let value = {};
        if (option.key === 'parameters') {
            setParameters([]);
            value = [];
        } else if (option.key === 'responses') {
            value = {
                200: {
                    description: 'Success',
                    content: {
                        'application/json': {
                            schema: {
                                type: 'object',
                                properties: {},
                            },
                        },
                    },
                },
                400: {
                    description: 'Bad Request',
                    content: {
                        'application/json': {
                            schema: {
                                type: 'object',
                                properties: {},
                            },
                        },
                    },
                },
                500: {
                    description: 'Server Error',
                    content: {
                        'application/json': {
                            schema: {
                                type: 'object',
                                properties: {},
                            },
                        },
                    },
                },
            };
        }

        updateOperation({type: 'add-op-block', block: {key: option.key, value}});
    }

    function handleOperationBlockRemove() {
        const changeSet = {
            op: 'remove-block',
            name,
        };
        return onSave && onSave(changeSet);
    }

    const blockMenu = {
        onClick: handleAddBlock,
        items: [
            {key: 'parameters', label: 'Parameters'},
            ...(['post', 'put', 'patch'].indexOf(name) !== -1 ? [{key: 'requestBody', label: 'Request Body'}] : []),
            {key: 'responses', label: 'Responses'},
        ],
    };

    useEffect(() => {
        if (op) {
            updateOperation({type: 'init', op});
        } else {
            updateOperation({type: 'init', op: null});
        }
    }, [op]);

    useEffect(() => {
        if (editedOperation) {
            async function transformParameters() {
                setParameters(await state.transformParameters(editedOperation.parameters, params));
            }

            if (editedOperation.parameters) {
                transformParameters();
            }
        }
    }, [editedOperation]);

    // Counts driving the collapsible section labels — same grammar as
    // SchemaBlock so the user sees a uniform "Section (N)" pattern across
    // model / API / webhook views.
    const parameterCount = parameters ? parameters.length : 0;
    const responseCount = editedOperation.responses
        ? Object.keys(editedOperation.responses).length
        : 0;
    const hasRequestBody = !!editedOperation.requestBody;
    const supportsRequestBody = ['post', 'put', 'patch'].indexOf(name) !== -1;

    // Default-open rule mirrors the model's: populated text panels open;
    // body sections that exist open; the focal section (Responses, the
    // operation's payload contract) is always open.
    const defaultActiveKey = useMemo(() => {
        const keys = ['responses'];
        if (editedOperation.summary || editedOperation.description) keys.unshift('header');
        if (parameterCount > 0) keys.push('parameters');
        if (hasRequestBody) keys.push('request');
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [op]);

    const items = [
        {
            key: 'header',
            label: 'Description',
            children: (
                <OperationHeaderBlock
                    operation={editedOperation}
                    name={name}
                    path={path}
                    color={color}
                    onChange={updateOperation}
                    delegatedEditMode={editMode}
                    imageRootPath={imageRootPath}
                    mobile={mobile}
                />
            ),
        },
        (parameters || editMode) && {
            key: 'parameters',
            label: `Parameters${parameterCount > 0 ? ` (${parameterCount})` : ''}`,
            children: <ParameterTable parameters={parameters || []} editMode={editMode} onChange={updateOperation} />,
        },
        (hasRequestBody || (supportsRequestBody && editMode)) && {
            key: 'request',
            label: 'Request Body',
            // Delete button rides in the Collapse panel header (next to the
            // caret) instead of inside the body — it's where ToggleSection
            // used to put it. Click stops propagation in the button itself
            // so the panel doesn't toggle when the user fires Popconfirm.
            extra: hasRequestBody && editMode
                ? <RequestBodyDeleteButton onChange={updateOperation} />
                : undefined,
            children: <RequestBody body={editedOperation.requestBody} editMode={editMode} onChange={updateOperation} />,
        },
        {
            key: 'responses',
            label: `Responses${responseCount > 0 ? ` (${responseCount})` : ''}`,
            children: <Responses responses={editedOperation.responses} editMode={editMode} onChange={updateOperation} />,
        },
    ].filter(Boolean);

    return editedOperation ? (
        <Block
            name={`operation ${name} ${path}`}
            className={classes}
            onCancel={handleCancel}
            onSave={handleSave}
            addOptions={blockMenu}
            onEdit={triggerEdit}
            onRemoveBlock={handleOperationBlockRemove}
            mobile={mobile}>
            <Collapse
                className="operationSections"
                bordered={false}
                defaultActiveKey={defaultActiveKey}
                items={items}
            />
        </Block>
    ) : null;
}
